import { GameState } from '../types/game';
import { gameDB } from './db';

const STORAGE_KEY_PREFIX = 'cryptoClicker_';

export const gameService = {
  async saveGameState(walletAddress: string, state: GameState): Promise<void> {
    if (!walletAddress) return;
    
    try {
      // Save to localStorage
      localStorage.setItem(
        `${STORAGE_KEY_PREFIX}gameState_${walletAddress}`,
        JSON.stringify(state)
      );
      
      // Save individual values
      localStorage.setItem(`${STORAGE_KEY_PREFIX}balance_${walletAddress}`, state.balance.toString());
      localStorage.setItem(`${STORAGE_KEY_PREFIX}coinsPerClick_${walletAddress}`, state.coinsPerClick.toString());
      localStorage.setItem(`${STORAGE_KEY_PREFIX}clicks_${walletAddress}`, state.clicks.toString());
      localStorage.setItem(`${STORAGE_KEY_PREFIX}miningClicks_${walletAddress}`, state.miningClicks.toString());
      localStorage.setItem(`${STORAGE_KEY_PREFIX}tokens_${walletAddress}`, state.balance.toString());

      // Sync with database
      await gameDB.saveUserProgress(walletAddress, {
        balance: state.balance,
        pending_wei: state.pendingWei.toString()
      });
    } catch (error) {
      console.error('Error saving game state:', error);
      throw error;
    }
  },

  async loadGameState(walletAddress: string): Promise<GameState | null> {
    if (!walletAddress) return null;
    
    try {
      // Load from database first
      const dbState = await gameDB.getUserProgress(walletAddress);
      
      // Load from localStorage as backup
      const savedState = localStorage.getItem(`${STORAGE_KEY_PREFIX}gameState_${walletAddress}`);
      const parsedState = savedState ? JSON.parse(savedState) : null;
      
      // Merge states, prioritizing database values
      const balance = dbState?.balance ?? parseInt(localStorage.getItem(`${STORAGE_KEY_PREFIX}balance_${walletAddress}`) || '0');
      const coinsPerClick = parseInt(localStorage.getItem(`${STORAGE_KEY_PREFIX}coinsPerClick_${walletAddress}`) || '1');
      const clicks = parseInt(localStorage.getItem(`${STORAGE_KEY_PREFIX}clicks_${walletAddress}`) || '0');
      const miningClicks = parseInt(localStorage.getItem(`${STORAGE_KEY_PREFIX}miningClicks_${walletAddress}`) || '0');
      const tokens = dbState?.balance ?? parseInt(localStorage.getItem(`${STORAGE_KEY_PREFIX}tokens_${walletAddress}`) || '0');
      
      return {
        ...(parsedState || {}),
        balance,
        coinsPerClick,
        clicks,
        miningClicks,
        tokens,
        pendingWei: dbState?.pending_wei || '0'
      };
    } catch (error) {
      console.error('Error loading game state:', error);
      return null;
    }
  },

  saveClicks(walletAddress: string, clicks: number): void {
    if (!walletAddress) return;
    localStorage.setItem(`${STORAGE_KEY_PREFIX}clicks_${walletAddress}`, clicks.toString());
  },

  loadClicks(walletAddress: string): number {
    if (!walletAddress) return 0;
    const savedClicks = localStorage.getItem(`${STORAGE_KEY_PREFIX}clicks_${walletAddress}`);
    return savedClicks ? parseInt(savedClicks, 10) : 0;
  },

  async clearGameData(walletAddress: string): Promise<void> {
    if (!walletAddress) return;
    
    const keys = [
      'gameState',
      'balance',
      'coinsPerClick',
      'clicks',
      'miningClicks',
      'tokens'
    ];
    
    // Clear localStorage
    keys.forEach(key => {
      localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}_${walletAddress}`);
    });

    // Clear database
    try {
      await gameDB.clearData(walletAddress);
    } catch (error) {
      console.error('Error clearing database:', error);
      throw error;
    }
  },

  saveBalance(walletAddress: string, balance: number): void {
    if (!walletAddress) return;
    localStorage.setItem(`${STORAGE_KEY_PREFIX}balance_${walletAddress}`, balance.toString());
    localStorage.setItem(`${STORAGE_KEY_PREFIX}tokens_${walletAddress}`, balance.toString());
  },

  loadBalance(walletAddress: string): number {
    if (!walletAddress) return 0;
    const savedBalance = localStorage.getItem(`${STORAGE_KEY_PREFIX}balance_${walletAddress}`);
    return savedBalance ? parseInt(savedBalance, 10) : 0;
  },

  saveCoinsPerClick(walletAddress: string, coinsPerClick: number): void {
    if (!walletAddress) return;
    localStorage.setItem(`${STORAGE_KEY_PREFIX}coinsPerClick_${walletAddress}`, coinsPerClick.toString());
  },

  loadCoinsPerClick(walletAddress: string): number {
    if (!walletAddress) return 1;
    const savedCoinsPerClick = localStorage.getItem(`${STORAGE_KEY_PREFIX}coinsPerClick_${walletAddress}`);
    return savedCoinsPerClick ? parseInt(savedCoinsPerClick, 10) : 1;
  },

  saveMiningClicks(walletAddress: string, miningClicks: number): void {
    if (!walletAddress) return;
    localStorage.setItem(`${STORAGE_KEY_PREFIX}miningClicks_${walletAddress}`, miningClicks.toString());
  },

  loadMiningClicks(walletAddress: string): number {
    if (!walletAddress) return 0;
    const savedMiningClicks = localStorage.getItem(`${STORAGE_KEY_PREFIX}miningClicks_${walletAddress}`);
    return savedMiningClicks ? parseInt(savedMiningClicks, 10) : 0;
  }
};